// İSG Portal — İş İzni Tipleri (Permit Types — tenant-scoped) API çağrıları + tipleri.
// **İSG uzmanı / firma admin tarafı** (web-expert): firma kendi iş izni tiplerini
// (`PermitType`) tanımlar ve her tip için hangi belgelerin isteneceğini §27 belge türü kaydından
// `DocumentTypeRef` (`{ entity, code }`) olarak seçer. Bir tip iş izinlerinin (§14) sınıflamasıdır:
// `WorkPermit.permitTypeUuid` bu tipe bağlanır (eski sabit `workType` enum'unun yerini aldı —
// sahip kararı 2026-08-11).
//
// KAYNAK: api-contract.md §22 (İş İzni Tipleri). Backend kanonu; burada **varsayım YOK** —
// endpoint yolları, istek/yanıt alanları ve kimlik kuralları (Result unwrap `value`; Create →
// `value.uuid` + 201; liste → `value` + `total`/`filteredTotal`) doğrudan sözleşmedendir.
// Serileştirme camelCase (§1).
//
// **Tenant izolasyonu (§4b/§22).** Tüm uçlar çağıranın firmasına (token `CompanyId`) kapanır;
// istemci firma kimliği GÖNDERMEZ — sunucu token'dan çözer. Başka firmanın tipi hiçbir koşulda
// görünmez/güncellenmez/silinmez (yok gibi `404 PERMIT_TYPE_NOT_FOUND`). Silme **soft-delete**'tir
// (kullanımdaki tip silinse de mevcut izinler etkilenmez — `permitTypeName` çözülmeye devam eder).

import { api } from './client';
import type { HazardClass } from './contractors';
import type { DocumentTypeRef } from './documentTypes';
import type { Empty } from './envelope';
import type { ProtectiveEquipmentRefDto } from './protectiveEquipment';

/**
 * Tipin bağlı olduğu bir kontrol itemı — api-contract §25.2 (`PermitTypeControlItemDto`). Tip
 * detayında (`controlItems`) yer alır; `{ uuid, text }` (bağ × katalog inner join; atama sırasında).
 * KKD referansı (`ProtectiveEquipmentRefDto` — `{ uuid, name }`) `protectiveEquipment.ts`'tedir.
 */
export interface PermitTypeControlItemDto {
  uuid: string;
  text: string;
}

/**
 * İş izni tipi — **liste satırı** (api-contract §22 `PermitTypeDto`). `hazardClass` tip düzeyi
 * varsayılan (opsiyonel; izin bazlı değer §14'te tutulur); `requiredDocumentCount` = tipin istediği
 * (silinmemiş) belge sayısı. Sıralı iç `Id` istemciye **sızmaz** (yalnız `uuid`). `createdAt` UTC ISO (§4).
 */
export interface PermitTypeDto {
  uuid: string;
  name: string;
  description: string | null;
  hazardClass: HazardClass | null;
  isActive: boolean;
  requiredDocumentCount: number;
  createdAt: string | null;
}

/**
 * İş izni tipi — **detay** görünümü (api-contract §22 `PermitTypeDetailDto`; `GET /permit-types/{uuid}`,
 * create/update dönüşü). Tip alanları + **istenen belge listesi** (`requiredDocuments`). Yeni tip
 * `isActive = true`.
 */
export interface PermitTypeDetailDto {
  uuid: string;
  name: string;
  description: string | null;
  hazardClass: HazardClass | null;
  isActive: boolean;
  createdAt: string | null;
  /**
   * İstenen belgeler — §22 `DocumentTypeRef[]` (`entity` ∈ personnel/vehicle + §27 kayıt `code`'u).
   * Kayıtta olmayan ya da pasif bir saklı kod okumada DÜŞÜRÜLMEZ (§27.0(6)); `PUT` onu korur.
   */
  requiredDocuments: DocumentTypeRef[];
  /**
   * Tipin istediği KKD listesi — api-contract §24.2 (`requiredProtectiveEquipment`;
   * `ProtectiveEquipmentRefDto[]` = `[{ uuid, name }]`; bağ × katalog inner join; soft-delete edilmiş
   * katalog ögesi düşer; ad artan; bağlanmamışsa `[]`). Form seçicisini önyükler (replace).
   */
  requiredProtectiveEquipment: ProtectiveEquipmentRefDto[];
  /**
   * Tipin bağlı kontrol itemları — api-contract §25.2 (`controlItems`; `PermitTypeControlItemDto[]` =
   * `[{ uuid, text }]`; bağ × katalog inner join; atama sırasında; bağlanmamışsa `[]`). Form
   * seçicisini önyükler (replace).
   */
  controlItems: PermitTypeControlItemDto[];
}

/**
 * Tip oluşturma isteği — api-contract §22.1 (`POST /permit-types`). `name` zorunlu; `hazardClass`
 * verilirse 1-3; her istenen belge `(entity, code)` §27.6 doğrulamasından geçer (`REQUIRED`/
 * `INVALID`/`INACTIVE` → `400`, alan yolu `requiredDocuments[i].code` / `.entity`, sıfır tabanlı).
 * Yinelenen çift idempotent atlanır. Yeni tip `isActive = true` (istekte taşınmaz). İstenen belge
 * seti verilmezse boş kurulur.
 */
export interface PermitTypeCreateRequest {
  name: string;
  description?: string | null;
  hazardClass?: HazardClass | null;
  requiredDocuments?: DocumentTypeRef[];
  /**
   * İstenen KKD seti — api-contract §24.2 (`protectiveEquipmentUuids: string[]`; **replace**; boş
   * liste = temizle; atlanabilir). Her uuid çağıranın firmasında olmalı (yok/başka firma →
   * `404 PROTECTIVE_EQUIPMENT_NOT_FOUND`; kısmi yazma yok — tip bile kurulmaz).
   */
  protectiveEquipmentUuids?: string[];
  /**
   * Bağlanacak kontrol itemları — api-contract §25.2 (`controlItemUuids: string[]`; **replace**; boş
   * liste = temizle; atlanabilir). Her uuid çağıranın firmasında olmalı (yok/başka firma →
   * `404 CONTROL_ITEM_NOT_FOUND`; kısmi yazma yok).
   */
  controlItemUuids?: string[];
}

/**
 * Tip güncelleme isteği — api-contract §22.1 (`PUT /permit-types/{uuid}`). Tip alanları +
 * **istenen belge seti `replace`** (eski set soft-delete, yeni set yazılır). `isActive` ile tip
 * aktif/pasif yapılabilir (**pasif tip yeni/güncellenen izinde seçilemez** — §14). Doğrulama create ile aynı.
 */
export interface PermitTypeUpdateRequest {
  name: string;
  description?: string | null;
  hazardClass?: HazardClass | null;
  isActive: boolean;
  /**
   * `replace`. Tipin ZATEN sakladığı pasif bir kod korunur (ilgisiz düzenleme yine kaydedilir);
   * yalnız yeni eklenen pasif kod `INACTIVE` alır (§22).
   */
  requiredDocuments?: DocumentTypeRef[];
  /** İstenen KKD seti — **replace** (boş = temizle; §24.2). Doğrulama create ile aynı. */
  protectiveEquipmentUuids?: string[];
  /** Bağlanacak kontrol itemları — **replace** (boş = temizle; §25.2). Doğrulama create ile aynı. */
  controlItemUuids?: string[];
}

/**
 * Tip liste/filtre parametreleri — api-contract §22.1 (`GET /permit-types`) + §3 paging.
 * `isActive` (verilmezse hepsi), `search` (ad substring, case-insensitive). Sıra backend'de ad artan.
 */
export interface PermitTypeListParams {
  isActive?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * `GET /permit-types` (`list`) — firmanın iş izni tipleri (sayfalı + filtreli). Sıra ad artan
 * (istemci sort göndermez). `isActive` verilmezse hepsi döner.
 */
export function listPermitTypes(params: PermitTypeListParams = {}) {
  return api.list<PermitTypeDto>('permit-types', {
    searchParams: {
      isActive: params.isActive,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * `GET /permit-types/{uuid}` (`get`) — tek tip + **istenen belge listesi**. Yok/kapsam dışı →
 * `404 PERMIT_TYPE_NOT_FOUND`.
 */
export function getPermitType(uuid: string) {
  return api.get<PermitTypeDetailDto>(`permit-types/${uuid}`);
}

/**
 * `POST /permit-types` (`create`) — tip oluşturur (`isActive = true`); yeni kimlik `value.uuid`
 * (201). Doğrulama hataları → `400 VALIDATION_FAILED`.
 */
export function createPermitType(body: PermitTypeCreateRequest) {
  return api.post<PermitTypeDetailDto>('permit-types', { json: body });
}

/**
 * `PUT /permit-types/{uuid}` (`update`) — tip alanları + istenen belge setini **replace** eder.
 * Yok/kapsam dışı → `404 PERMIT_TYPE_NOT_FOUND`.
 */
export function updatePermitType(uuid: string, body: PermitTypeUpdateRequest) {
  return api.put<PermitTypeDetailDto>(`permit-types/${uuid}`, { json: body });
}

/**
 * `DELETE /permit-types/{uuid}` (`delete`) — tipi **soft-delete** eder (istenen belge seti de).
 * Kullanımdaki tip silinse de mevcut izinler etkilenmez (`permitTypeName` çözülmeye devam eder).
 * Yok/kapsam dışı → `404 PERMIT_TYPE_NOT_FOUND`.
 */
export function deletePermitType(uuid: string) {
  return api.delete<Empty>(`permit-types/${uuid}`);
}
